import React from 'react';

export const dracoSettingsArticle = {
  id: 'advanced-draco-settings',
  title: 'Draco Compression Settings',
  description: 'Tweak Draco parameters for the smallest meshes without visible artefacts.',
  seoTitle: 'Draco Compression Settings: Compression Level & Quantization',
  seoDescription: 'Which Draco compression level and quantization bits to use for GLB and glTF models, how each setting affects file size and quality, and recommended values.',
  category: 'advanced',
  lastUpdated: '2025-06-29',
  tags: ['draco', 'compression', 'mesh'],
  content: () => (
    <div>
  <div className="space-y-6 text-muted-foreground leading-relaxed">
        <p>
          <a href="https://google.github.io/draco/" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/90">Draco</a>{' '}
          encodes mesh geometry using predictive coding + entropy compression.  Typical savings are 80-95 % on raw
          vertex data.
        </p>

  <h2 className="text-xl font-semibold text-foreground mb-3">How Draco works (TL;DR)</h2>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li><strong>Quantisation</strong> – Converts floats → ints within a bit budget (8-16 bits).</li>
          <li><strong>Connectivity encode</strong> – Reorders triangles (Edgebreaker) to exploit locality.</li>
          <li><strong>Entropy encode</strong> – Final pass with LZ77 + arithmetic coding.</li>
        </ul>
  <p className="text-sm text-muted">Read Google's excellent overview on their <a href="https://opensource.googleblog.com/2017/01/introducing-draco-compression-for-3d.html" target="_blank" rel="noopener noreferrer" className="underline">Open Source Blog</a>.</p>

  <h2 className="text-xl font-semibold text-foreground mb-3">Key Parameters</h2>
  <table className="w-full text-sm text-left border-collapse border border">
          <thead>
            <tr className="bg-background-elevated text-foreground">
              <th className="p-2 border border">Parameter</th>
              <th className="p-2 border border">Range</th>
              <th className="p-2 border border">Effect</th>
              <th className="p-2 border border">Typical</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="p-2 border border">Compression level</td>
              <td className="p-2 border border">1-10</td>
              <td className="p-2 border border">Higher spends more CPU, ~5-20 % extra savings</td>
              <td className="p-2 border border">8</td>
            </tr>
            <tr>
              <td className="p-2 border border">Quantisation bits</td>
              <td className="p-2 border border">8-16</td>
              <td className="p-2 border border">Lower → smaller files but visible stair-stepping</td>
              <td className="p-2 border border">14</td>
            </tr>
          </tbody>
        </table>

  <h2 className="text-xl font-semibold text-foreground mb-3">Choosing Values</h2>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li><strong>Static props</strong> – Go aggressive: <code>level 9</code>, <code>quant 10</code>.</li>
          <li><strong>Hero assets</strong> – Moderate: <code>level 7</code>, <code>quant 14</code>.</li>
          <li><strong>Architectural CAD</strong> – Precision matters: <code>level 6</code>, <code>quant 16</code>.</li>
        </ul>

  <h2 className="text-xl font-semibold text-foreground mb-3">Tips & Gotchas</h2>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>Draco does <em>not</em> support <code>morphTargets</code>; keep originals if you need shape keys.</li>
          <li>Game engines need a decoder.  <a href="https://threejs.org/docs/#examples/en/loaders/DRACOLoader" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/90">three.js DRACOLoader</a> auto-loads WASM  &lt; 300 KB.</li>
          <li>Precision loss is most visible on hard-edged mechanical parts.  Visual check in wireframe mode.</li>
        </ul>
      </div>
    </div>
  )
}; 